import { Badge } from "ui/components/badge";
import { NavItem } from "@/components/nav/item";
import { ShimmerEffect } from "@/components/shimmer";
import { useCount } from "hooks/src/useCount";

/**
 * A common navigation item component that handles badge display with loading states.
 * Pass `collection` + `filters` to let the component fetch the count internally via
 * `useCount`. For counts that come from custom endpoints or context, pass `count` and
 * `isCountLoading` directly as an override.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the navigation item
 * @param {React.ComponentType} props.icon - The icon component to display
 * @param {string} props.url - The URL to navigate to
 * @param {string} [props.collection] - Directus collection to count items from
 * @param {Object} [props.filters] - Directus filter object applied to the count query
 * @param {Function} [props.fetchCount] - Custom fetch function forwarded to useCount (for non-standard endpoints)
 * @param {number|null} [props.count] - Override: count value (from context / custom hooks)
 * @param {boolean} [props.isCountLoading] - Override: loading state for the count
 * @param {Object} [props.requiredPermissions] - Permission requirements for the nav item
 * @param {boolean} [props.matchExact] - Whether to match the URL exactly
 * @param {React.ReactNode} [props.children] - Child components (for sub-navigation)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.badgeProps] - Additional props for the Badge component
 */
export function NavItemWithBadge({
  title,
  icon,
  url,
  collection,
  filters,
  fetchCount,
  count: countProp,
  isCountLoading: isCountLoadingProp = false,
  requiredPermissions,
  matchExact,
  children,
  className,
  badgeProps = {},
  ...rest
}) {
  const {
    count: fetchedCount,
    isLoading: isFetchedLoading,
  } = useCount({ endpoint: collection, filters, fetchCount });

  // Use internally-fetched count when collection is provided; fall back to prop-supplied values
  const count = collection ? fetchedCount : countProp;
  const isCountLoading = collection ? isFetchedLoading : isCountLoadingProp;
  // Create the badge element based on loading state and count
  const badge = isCountLoading ? (
    <ShimmerEffect className="w-8 h-4 rounded-full" />
  ) : count ? (
    <Badge
      variant="outline"
      className="rounded p-1 min-w-2"
      {...badgeProps}
    >
      {count}
    </Badge>
  ) : null;

  // Wrap title with flex layout if we have a badge
  const titleWithBadge = badge ? (
    <span className="flex justify-between">{title}</span>
  ) : (
    title
  );

  return (
    <NavItem
      title={titleWithBadge}
      badge={badge}
      icon={icon}
      url={url}
      requiredPermissions={requiredPermissions}
      matchExact={matchExact}
      className={className}
      {...rest}
    >
      {children}
    </NavItem>
  );
}
